import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    useTheme,
} from '@mui/material';
import { FlagIcon } from 'src/components/iconify/flagIcon';
import HorizontalPager from 'src/components/horizontal-pager';
import PulseDot from 'src/components/pulse-dot';
import { CountryFilterProps, CountryItem } from '../types';
import { getLevelColor } from '../utils';
import countriesCatalog from 'src/utils/countries.json';

export function CountryFilter({
    countries,
    selectedCountry,
    onCountryChange,
    backgroundColor = 'transparent',
    enableDeepLinking = true,
    renderCountryLabel
}: CountryFilterProps) {
    const theme = useTheme();

    // Map region names to country codes
    const getCountryCodeFromRegionName = (regionName: string, countries: any[]): string | null => {
        if (!countries || countries.length === 0) return null;

        // Try exact match first
        const exactMatch = countries.find(
            (c) => c.country_name?.toLowerCase() === regionName?.toLowerCase()
        );
        if (exactMatch) return exactMatch.country_code;

        // Try partial match (e.g., "France" matches "France")
        const partialMatch = countries.find(
            (c) => regionName?.toLowerCase().includes(c.country_name?.toLowerCase()) ||
                c.country_name?.toLowerCase().includes(regionName?.toLowerCase())
        );
        if (partialMatch) return partialMatch.country_code;

        // Fallback: try to extract code from region name if it looks like a code
        if (regionName && regionName.length === 2) {
            return regionName.toUpperCase();
        }

        return null;
    };


    if (countries.length <= 1) {
        return null;
    }

    const codeToName = useMemo(() => {
        const map = new Map<string, string>();
        countries.forEach((c) => {
            map.set(String(c.country_code).toUpperCase(), c.country_name);
        });
        return map;
    }, [countries]);

    const [didApplyDeepLink, setDidApplyDeepLink] = useState(false);

    useEffect(() => {
        if (didApplyDeepLink || !enableDeepLinking) return;
        try {
            const params = new URLSearchParams(window.location.search);
            const codeParam = params.get('country');
            if (codeParam) {
                const countryName = codeToName.get(codeParam.toUpperCase());
                if (countryName) {
                    onCountryChange(selectedCountry === countryName ? selectedCountry : countryName);
                }
            }
        } catch { }
        setDidApplyDeepLink(true);
    }, [codeToName, onCountryChange, didApplyDeepLink, selectedCountry, enableDeepLinking]);

    return (
        <Paper sx={{ p: 0, overflow: 'hidden', backgroundColor: backgroundColor, borderBottom: "1px solid rgba(145, 158, 171, 0.20)", px: '24px' }}>
            <HorizontalPager
                items={countries}
                getItemKey={(c) => c.country_code}
                activeIndex={selectedCountry ? countries.findIndex((c) => c.country_name === selectedCountry) : null}
                renderItem={(c: CountryItem) => {
                    const countryCode = getCountryCodeFromRegionName(
                        c.country_name,
                        countries
                    );

                    return (
                        <Button
                            key={c.country_code}
                            variant="text"
                            color="inherit"
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                            onClick={() =>
                                onCountryChange(
                                    selectedCountry === c.country_name ? null : c.country_name
                                )
                            }
                            sx={{
                                px: 2,
                                gap: 1.2,
                                minHeight: 36,
                                borderRadius: 0,
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    bottom: 0,
                                    height: '2px',
                                    width: selectedCountry === c.country_name ? '80%' : 0,
                                    backgroundColor:
                                        theme.palette.mode === 'dark' ? '#fff' : '#000',
                                },
                                '&:hover': { backgroundColor: 'transparent' },
                            }}
                        >
                            {countryCode && <FlagIcon code={countryCode} />}

                            <Typography
                                sx={{
                                    fontWeight: 400,
                                    fontSize: ".875rem",
                                }}
                                color={selectedCountry === c.country_name ? "black" : "#919EAB"}
                            >
                                {renderCountryLabel
                                    ? renderCountryLabel(c, selectedCountry === c.country_name)
                                    : c.country_name}
                            </Typography>

                            <PulseDot
                                color={c.level ? getLevelColor(c.level, theme) : '#fff'}
                                size={18}
                            />
                        </Button>
                    );
                }}
            />
        </Paper>
    );
}
